<script setup lang="ts">
/**
 * Aperçu de l'action UNIQUE « Ajouter à mon compte » (DEV-714, palier 2).
 *
 * Le client a UNE seule décision : « est-ce mon compteur ? ajoute-le ». Le
 * distinguo interne rattacher (déjà sur Homeys) vs créer (inconnu) reste côté
 * back — invisible ici. Cette modale EXPLIQUE ce qui va se passer (demande de
 * consentement de titularité → visibilité), sans rien déclencher : le
 * consentement réel est envoyé séparément (aucune action sortante surprise).
 */
import { computed } from "vue"
import type { FactureItem } from "@modules/courtage/types/facture-espace"

const props = defineProps<{
    modelValue: boolean
    facture: FactureItem | null
}>()

const emit = defineEmits<{
    (e: "update:modelValue", value: boolean): void
    (e: "confirm", facture: FactureItem): void
}>()

defineOptions({ name: "ModalAjouterCompte" })

const titre = computed(
    () =>
        props.facture?.compteur_label ??
        props.facture?.adresse_facture ??
        "Compteur non rattaché"
)

function close(): void {
    emit("update:modelValue", false)
}

function confirmer(): void {
    if (props.facture) emit("confirm", props.facture)
}
</script>

<template>
    <div
        v-if="modelValue && facture"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
        @click.self="close"
    >
        <div class="w-full max-w-md rounded-xl bg-white shadow-xl">
            <header
                class="flex items-start justify-between gap-3 border-b border-neutral-100 px-6 py-4"
            >
                <h2 class="m-0 text-lg font-medium text-primary-900">
                    Ajouter ce compteur à votre compte
                </h2>
                <button
                    type="button"
                    class="text-neutral-400 hover:text-neutral-700"
                    aria-label="Fermer"
                    @click="close"
                >
                    <i class="pi pi-times" aria-hidden="true" />
                </button>
            </header>

            <div class="px-6 py-5">
                <div class="rounded-lg bg-neutral-50 px-4 py-3">
                    <div class="font-medium text-neutral-800">{{ titre }}</div>
                    <div class="mt-0.5 font-mono text-xs text-neutral-500">
                        {{ facture.pdl }} · {{ facture.fournisseur ?? "—" }}
                    </div>
                </div>

                <p class="mt-4 text-sm leading-relaxed text-neutral-600">
                    En l'ajoutant, vous déclarez être
                    <strong>titulaire</strong> de ce compteur. Une
                    <strong>demande de consentement</strong> vous sera envoyée à
                    signer ; une fois signée, les montants et les factures de ce
                    compteur deviennent visibles dans votre espace.
                </p>

                <p class="mt-3 flex items-start gap-2 text-xs text-neutral-400">
                    <i class="pi pi-info-circle mt-0.5" aria-hidden="true" />
                    <span>
                        Aucune signature ici : nous préparons la demande et
                        revenons vers vous pour la valider.
                    </span>
                </p>
            </div>

            <footer
                class="flex justify-end gap-2 border-t border-neutral-100 px-6 py-4"
            >
                <button
                    type="button"
                    class="rounded-md px-4 py-2 text-sm font-medium text-neutral-600 hover:bg-neutral-100"
                    @click="close"
                >
                    Annuler
                </button>
                <button
                    type="button"
                    class="inline-flex items-center gap-1.5 rounded-md bg-primary-800 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
                    @click="confirmer"
                >
                    <i class="pi pi-plus" aria-hidden="true" />
                    Demander l'ajout
                </button>
            </footer>
        </div>
    </div>
</template>
